"use client"

import { Container, Title, Text, Button, Group, Box } from "@forestsync/ui"
import { TbArrowRight, TbChevronDown } from "react-icons/tb"
import Link from "next/link"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { useAnalytics } from "@/components/analytics/AnalyticsProvider"
import { useAuth } from "@/components/layout/AuthProvider"

export function LandingHero() {
  const t = useTranslations("landing")
  const { trackEvent } = useAnalytics()
  const { isAuthenticated } = useAuth()

  return (
    <Box style={{ position: "relative", minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center" }}>
      {/* Background photo */}
      <Image
        src="/images/redd.png"
        alt="Forest carbon project"
        fill
        priority
        style={{ objectFit: "cover", objectPosition: "center" }}
      />

      {/* Dark gradient overlay */}
      <Box
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to bottom, rgba(13,38,24,0.72) 0%, rgba(13,38,24,0.88) 100%)",
          zIndex: 1,
        }}
      />

      {/* Content */}
      <Box style={{ position: "relative", zIndex: 2, width: "100%" }}>
        <Container size="lg" py={80}>
          <Text
            size="sm"
            ta="center"
            mb="lg"
            style={{ color: "rgba(255,255,255,0.7)", fontStyle: "italic", letterSpacing: "0.02em" }}
          >
            {t("heroEyebrow")}
          </Text>

          <Title
            order={1}
            ta="center"
            style={{
              color: "white",
              fontWeight: 900,
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
              letterSpacing: "-2px",
              lineHeight: 1.1,
              maxWidth: 800,
              margin: "0 auto",
            }}
          >
            {t("heroTitle")}
          </Title>

          <Text
            ta="center"
            size="lg"
            mt="xl"
            style={{
              color: "rgba(255,255,255,0.82)",
              maxWidth: 620,
              margin: "24px auto 0",
              lineHeight: 1.7,
            }}
          >
            {t("heroSubtitle")}
          </Text>

          <Group justify="center" mt={48} gap="md" wrap="wrap">
            {isAuthenticated ? (
              <>
                <Button
                  size="xl"
                  radius="md"
                  color="forest"
                  autoContrast
                  component={Link}
                  href="/dashboard"
                  rightSection={<TbArrowRight size={20} />}
                  onClick={() => trackEvent({ name: "cta_click", cta_location: "hero", cta_text: "dashboard" })}
                >
                  {t("heroDashboardCta")}
                </Button>
                <Button
                  size="xl"
                  radius="md"
                  variant="outline"
                  color="white"
                  component={Link}
                  href="/market"
                  onClick={() => trackEvent({ name: "cta_click", cta_location: "hero", cta_text: "browse_auth" })}
                >
                  {t("heroBrowseAuthCta")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="xl"
                  radius="md"
                  color="forest"
                  autoContrast
                  component={Link}
                  href="/market"
                  rightSection={<TbArrowRight size={20} />}
                  onClick={() => trackEvent({ name: "cta_click", cta_location: "hero", cta_text: "browse_guest" })}
                >
                  {t("heroBrowseCta")}
                </Button>
                <Button
                  size="xl"
                  radius="md"
                  variant="outline"
                  color="white"
                  component={Link}
                  href="/register"
                  onClick={() => trackEvent({ name: "cta_click", cta_location: "hero", cta_text: "create_account" })}
                >
                  {t("heroCreateCta")}
                </Button>
              </>
            )}
          </Group>

          <Group justify="center" mt={48}>
            <TbChevronDown size={24} style={{ color: "rgba(255,255,255,0.4)", animation: "bounce 2s infinite" }} />
          </Group>
        </Container>
      </Box>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
      `}</style>
    </Box>
  )
}
